// pattern: functional-core
// Parse Open-Meteo daily weather API responses into our schema

export interface WeatherDay {
	date: string; // YYYY-MM-DD
	temp_high_c: number | null;
	temp_low_c: number | null;
	precip_mm: number | null;
	conditions: string | null;
	wind_max_kmh: number | null;
}

// Open-Meteo WMO weather codes → human-readable conditions
const WMO_CONDITIONS: Record<number, string> = {
	0: "sunny",
	1: "mostly sunny",
	2: "partly cloudy",
	3: "cloudy",
	45: "foggy",
	48: "foggy",
	51: "drizzle",
	53: "drizzle",
	55: "drizzle",
	61: "rainy",
	63: "rainy",
	65: "heavy rain",
	66: "freezing rain",
	67: "freezing rain",
	71: "snowy",
	73: "snowy",
	75: "heavy snow",
	77: "snow grains",
	80: "rain showers",
	81: "rain showers",
	82: "heavy showers",
	85: "snow showers",
	86: "heavy snow showers",
	95: "stormy",
	96: "stormy",
	99: "stormy",
};

export function conditionFromWmoCode(code: number): string {
	return WMO_CONDITIONS[code] ?? "unknown";
}

interface OpenMeteoDaily {
	time: string[];
	temperature_2m_max: number[];
	temperature_2m_min: number[];
	precipitation_sum: number[];
	weather_code: number[];
	wind_speed_10m_max: number[];
}

export function parseOpenMeteoResponse(daily: OpenMeteoDaily): WeatherDay[] {
	const days: WeatherDay[] = [];
	for (let i = 0; i < daily.time.length; i++) {
		days.push({
			date: daily.time[i]!,
			temp_high_c: daily.temperature_2m_max[i] ?? null,
			temp_low_c: daily.temperature_2m_min[i] ?? null,
			precip_mm: daily.precipitation_sum[i] ?? null,
			conditions: conditionFromWmoCode(daily.weather_code[i] ?? -1),
			wind_max_kmh: daily.wind_speed_10m_max[i] ?? null,
		});
	}
	return days;
}

/** Build Open-Meteo API URL for a date range */
export function buildOpenMeteoUrl(lat: number, lng: number, from: string, to: string): string {
	return `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,weather_code,wind_speed_10m_max&start_date=${from}&end_date=${to}&timezone=auto`;
}

/** Weather emoji for quick display */
export function weatherEmoji(conditions: string | null): string {
	if (!conditions) return "";
	if (conditions.includes("sunny") || conditions.includes("mostly sunny")) return "\u2600\uFE0F";
	if (conditions.includes("partly cloudy")) return "\u26C5";
	if (conditions.includes("cloudy")) return "\u2601\uFE0F";
	if (conditions.includes("fog")) return "\u{1F32B}\uFE0F";
	if (conditions.includes("snow")) return "\u2744\uFE0F";
	if (conditions.includes("storm")) return "\u26C8\uFE0F";
	if (
		conditions.includes("rain") ||
		conditions.includes("drizzle") ||
		conditions.includes("shower")
	)
		return "\u{1F327}\uFE0F";
	return "";
}

/** Format temp for display (Celsius with optional Fahrenheit) */
export function formatTemp(celsius: number | null): string {
	if (celsius === null) return "";
	const f = Math.round((celsius * 9) / 5 + 32);
	return `${Math.round(celsius)}\u00B0C / ${f}\u00B0F`;
}
